// Render-scene modal. Opened from the "Render scene" tool-rail action.
//
// Three-step flow:
//   1. Idle — show a confirm screen describing what's about to happen and a
//      "Render" button. If the action block has no renderProfileId, the
//      confirm button is disabled and we explain why.
//   2. Submitting — pull the DModel JSON from Unity (callUnity GetSceneDModel),
//      POST it + the renderProfileId to /api/dmodel/render. Show a spinner.
//   3. Done — server responded with { id, viewUrl }; we open viewUrl in a
//      new tab and close the modal. If the new tab was blocked by the
//      browser's popup blocker, fall back to a link the operator can click.
//
// Errors at any stage drop back to the idle screen with an inline error and
// the Render button re-enabled so retries are cheap.

import { useState } from "react";
import type { ConfiguratorViewerHandle } from "../../../components/ConfiguratorViewer";
import { logDModelSummary } from "./debugDModel";

/** Operator-authorable text presented in the render-scene modal. Every field
 *  is optional and falls back to the English default below — the host resolves
 *  these from the modal:render-scene slot's text blocks (by key) so operators
 *  can reword / translate the presented copy. Transient status/error strings
 *  ("Sending…", network errors) are intentionally NOT here — they're system
 *  feedback, not presented content. */
export interface RenderSceneModalContent {
  heading?: string;
  intro?: string;
  renderLabel?: string;
  cancelLabel?: string;
  doneLabel?: string;
}

export interface RenderSceneModalProps {
  onClose: () => void;
  viewerRef: React.RefObject<ConfiguratorViewerHandle>;
  baseUrl?: string;
  /** The placing action block's renderProfileId. Empty / missing renders the
   *  modal in a "not configured" state — operator needs to set the profile
   *  in the dashboard's slot-mapping editor first. */
  renderProfileId?: string;
  /** Authorable presented copy; see RenderSceneModalContent. */
  content?: RenderSceneModalContent;
}

const RENDER_DEFAULTS: Required<RenderSceneModalContent> = {
  heading: "Render this scene",
  intro:
    "Send the current scene to be rendered. The render runs in the background and " +
    "may take a few minutes. We'll open a status page in a new tab — the image " +
    "appears there as soon as it's ready.",
  renderLabel: "Render",
  cancelLabel: "Cancel",
  doneLabel: "Done",
};

interface RenderResponse {
  id: string;
  viewUrl: string;
}

export function RenderSceneModal({
  onClose,
  viewerRef,
  baseUrl,
  renderProfileId,
  content,
}: RenderSceneModalProps) {
  // Presented copy — operator override or English default, per field.
  const t = { ...RENDER_DEFAULTS, ...content };
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // After a successful submit + new-tab open, we show a tiny "opened in a new
  // tab" confirmation with a fallback link in case the popup was blocked.
  const [opened, setOpened] = useState<{ viewUrl: string; blocked: boolean } | null>(null);

  const profileMissing = !renderProfileId;
  const absoluteUrl = (relative: string) => {
    const base = (baseUrl ?? "").replace(/\/+$/, "");
    return base + relative;
  };

  const submit = async () => {
    if (!renderProfileId) return;
    setSubmitting(true);
    setError(null);
    try {
      const v = viewerRef.current;
      if (!v) throw new Error("Viewer not active.");
      const dmodel = await v.callUnity("GetSceneDModel");
      logDModelSummary(dmodel);

      const res = await fetch(absoluteUrl("/api/dmodel/render"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ renderProfileId, dmodel }),
      });
      if (!res.ok) {
        let msg = `Render request failed (${res.status}).`;
        try {
          const body = await res.json();
          if (body?.error) msg = body.error;
        } catch { /* not JSON — keep the status message */ }
        throw new Error(msg);
      }
      const data = (await res.json()) as RenderResponse;
      // window.open returns null when the browser's popup blocker fires —
      // surface a clickable link in that case so the operator can still get
      // to the result page.
      const fullViewUrl = data.viewUrl.startsWith("http")
        ? data.viewUrl
        : absoluteUrl(data.viewUrl);
      const win = window.open(fullViewUrl, "_blank", "noopener,noreferrer");
      setOpened({ viewUrl: fullViewUrl, blocked: win == null });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sd-modal__inner sd-modal__inner--render-scene">
      <header className="sd-modal__header">
        <h2>{t.heading}</h2>
        <button type="button" className="sd-modal__close" onClick={onClose} aria-label="Close">×</button>
      </header>
      <div className="sd-modal__body">
        {opened ? (
          <div className="sd-render__done">
            <p>Your render is on the way.</p>
            {opened.blocked ? (
              <p className="sd-render__hint">
                We couldn't open a new tab automatically.{" "}
                <a href={opened.viewUrl} target="_blank" rel="noopener noreferrer">
                  Open the render status page
                </a>{" "}
                in a new tab.
              </p>
            ) : (
              <p className="sd-render__hint">
                The result is rendering in a new tab. You can keep working in the configurator
                while it processes — the page will refresh on its own when the image is ready.
              </p>
            )}
            <div className="sd-render__actions">
              <button type="button" className="sd-render__btn sd-render__btn--primary" onClick={onClose}>
                {t.doneLabel}
              </button>
            </div>
          </div>
        ) : profileMissing ? (
          <div className="sd-render__intro">
            <p className="sd-render__status sd-render__status--error">
              This render button hasn't been configured yet.
            </p>
            <p className="sd-render__hint">
              An admin needs to pick a render profile for this button in the dashboard's
              slot-mapping editor before it can be used.
            </p>
            <div className="sd-render__actions">
              <button type="button" className="sd-render__btn sd-render__btn--ghost" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="sd-render__intro">
            <p>{t.intro}</p>
            {error && (
              <p className="sd-render__status sd-render__status--error">{error}</p>
            )}
            <div className="sd-render__actions">
              <button
                type="button"
                className="sd-render__btn sd-render__btn--ghost"
                onClick={onClose}
                disabled={submitting}
              >
                {t.cancelLabel}
              </button>
              <button
                type="button"
                className="sd-render__btn sd-render__btn--primary"
                onClick={submit}
                disabled={submitting}
              >
                {submitting ? "Sending…" : t.renderLabel}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
